if (!customElements.get('next-day-delivery')) {
  class NextDayDelivery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.config = this._readConfig();
      if (!this.config) return;

      this.titleEl = this.querySelector('[data-ndd-title]');
      this.detailEl = this.querySelector('[data-ndd-detail]');
      this.estimateEl = this.querySelector('[data-ndd-estimate]');
      this.estimateShortEl = this.querySelector('[data-ndd-estimate-short]');
      this.howButton = this.querySelector('[data-ndd-how]');
      this.dialogEl = this.querySelector('[data-ndd-dialog]');
      this.closeButton = this.querySelector('[data-ndd-close]');
      this.currentVariantId = this.config.variantId;
      this.manualPostal = '';
      this.location = null;
      this.timer = null;

      this._onVariantChange = this._onVariantChange.bind(this);
      this._onPostalInput = this._onPostalInput.bind(this);
      this._onHowClick = this._onHowClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogBackdrop = this._onDialogBackdrop.bind(this);
      document.addEventListener('product:variant-change', this._onVariantChange);
      document.addEventListener('change', this._onPostalInput);
      document.addEventListener('input', this._onPostalInput);
      if (this.howButton) this.howButton.addEventListener('click', this._onHowClick);
      if (this.closeButton) this.closeButton.addEventListener('click', this._onDialogClose);
      if (this.dialogEl) this.dialogEl.addEventListener('click', this._onDialogBackdrop);

      this._init();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this._onVariantChange);
      document.removeEventListener('change', this._onPostalInput);
      document.removeEventListener('input', this._onPostalInput);
      if (this.howButton) this.howButton.removeEventListener('click', this._onHowClick);
      if (this.closeButton) this.closeButton.removeEventListener('click', this._onDialogClose);
      if (this.dialogEl) this.dialogEl.removeEventListener('click', this._onDialogBackdrop);
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
      this._stopTimer();
    }

    _readConfig() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    async _init() {
      this.location = await this._resolveLocation();
      this.geoEligible = this._shouldBypassGeo() || this._isGeoEligible();
      this._fillFaq();
      this._evaluate();
    }

    _shouldBypassGeo() {
      return Boolean(this.config.bypassGeo);
    }

    _onVariantChange(event) {
      const variant = event.detail?.variant;
      this.currentVariantId = variant?.id || null;
      this._evaluate();
    }

    _onPostalInput(event) {
      const field = event.target;
      if (!(field instanceof HTMLInputElement)) return;
      const name = (field.getAttribute('name') || '').toLowerCase();
      const autocomplete = (field.getAttribute('autocomplete') || '').toLowerCase();
      if (!name.includes('zip') && autocomplete !== 'postal-code') return;
      this.manualPostal = field.value || '';
      this.geoEligible = this._shouldBypassGeo() || this._isGeoEligible();
      this._evaluate();
    }

    _onHowClick() {
      if (this.dialogEl && typeof this.dialogEl.showModal === 'function') {
        this.dialogEl.showModal();
      }
    }

    _onDialogClose() {
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
    }

    _onDialogBackdrop(event) {
      if (event.target === this.dialogEl) this._onDialogClose();
    }

    _evaluate() {
      const mode = this._mode();
      this.mode = mode;
      if (mode) {
        this.setAttribute('data-ndd-mode', mode);
        this._renderCopy();
        this._show();
        this._startTimer();
        return;
      }
      this._hide();
      this._stopTimer();
    }

    // 'next' before the cutoff on a business day, 'second' after it (or on a
    // weekend/holiday) when 2-day delivery is enabled, null when hidden.
    _mode() {
      if (!this.config.enabled) return null;
      if (this.config.preorder) return null;
      if (!this._isVariantReady()) return null;
      if (!this.geoEligible) return null;
      if (this._isOrderWindowOpen()) return 'next';
      if (this.config.secondDayEnabled) return 'second';
      return null;
    }

    _isVariantReady() {
      if (!this.currentVariantId) return false;
      const entry = (this.config.variants || []).find((variant) => String(variant.id) === String(this.currentVariantId));
      if (!entry) return false;
      if (!entry.available) return false;
      if (entry.tracked) return Number(entry.quantity) > 0;
      return true;
    }

    _isOrderWindowOpen() {
      const remaining = this._msUntilCutoff();
      if (remaining <= 0) return false;
      const now = this._zonedParts(new Date());
      return this._isBusinessDay(now.year, now.month, now.day);
    }

    _msUntilCutoff() {
      const now = new Date();
      const parts = this._zonedParts(now);
      const cutoff = this._parseCutoff();
      const cutoffMs = this._zonedWallTimeToUtc(
        parts.year,
        parts.month,
        parts.day,
        cutoff.hour,
        cutoff.minute
      );
      return cutoffMs - now.getTime();
    }

    _parseCutoff() {
      const raw = String(this.config.cutoff || '13:00').trim();
      const match = raw.match(/^(\d{1,2})(?::(\d{2}))?$/);
      if (!match) return { hour: 13, minute: 0 };
      return {
        hour: Math.min(23, Number(match[1])),
        minute: Math.min(59, Number(match[2] || 0))
      };
    }

    _timeZone() {
      return this.config.timeZone || 'America/Vancouver';
    }

    _zonedParts(date) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this._timeZone(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
      const parts = {};
      formatter.formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = part.value;
      });
      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second)
      };
    }

    _zonedOffsetMs(date) {
      const parts = this._zonedParts(date);
      const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      return asUtc - date.getTime();
    }

    _zonedWallTimeToUtc(year, month, day, hour, minute) {
      const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
      const first = naiveUtc - this._zonedOffsetMs(new Date(naiveUtc));
      return naiveUtc - this._zonedOffsetMs(new Date(first));
    }

    _holidaySet() {
      if (this._holidays) return this._holidays;
      const values = String(this.config.holidays || '')
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      this._holidays = new Set(values);
      return this._holidays;
    }

    _isWeekend(year, month, day) {
      const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
      return weekday === 0 || weekday === 6;
    }

    _isBusinessDay(year, month, day) {
      if (this._isWeekend(year, month, day)) return false;
      const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return !this._holidaySet().has(key);
    }

    // Nth business day after today on the reference calendar (today excluded).
    _businessDayAhead(count) {
      const parts = this._zonedParts(new Date());
      let cursor = Date.UTC(parts.year, parts.month - 1, parts.day);
      let found = 0;
      for (let i = 0; i < 21; i += 1) {
        cursor += 24 * 60 * 60 * 1000;
        const date = new Date(cursor);
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();
        if (this._isBusinessDay(year, month, day)) {
          found += 1;
          if (found === count) return { year, month, day };
        }
      }
      return null;
    }

    _deliveryDate() {
      return this._businessDayAhead(this.mode === 'second' ? 2 : 1);
    }

    _formatDate(dateParts) {
      if (!dateParts) return '';
      const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 12, 0, 0));
      return new Intl.DateTimeFormat(this.config.locale || 'en-CA', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }).format(date);
    }

    _formatCutoff() {
      const cutoff = this._parseCutoff();
      const date = new Date(Date.UTC(2020, 0, 1, cutoff.hour, cutoff.minute, 0));
      const locale = this.config.locale === 'fr' ? 'fr-CA' : 'en-US';
      return new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: locale !== 'fr-CA',
        timeZone: 'UTC'
      }).format(date);
    }

    _formatCountdown(ms) {
      const totalMinutes = Math.max(0, Math.floor(ms / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours > 0) return `${hours}h ${minutes}min`;
      return `${minutes}min`;
    }

    _escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _replaceToken(value, token, replacement) {
      return String(value).split(token).join(replacement);
    }

    _cityName() {
      const city = String(this.location?.city || this.config.customerCity || '').trim();
      if (city) return city;
      return String(this.config.copy?.cityFallback || '').trim();
    }

    _fillFaq() {
      const body = this.querySelector('[data-ndd-how-body]');
      if (body) {
        let html = body.innerHTML;
        html = this._replaceToken(html, '[cutoff]', this._escapeHtml(this._formatCutoff()));
        html = this._replaceToken(html, '[regions]', this._escapeHtml(this.config.regions || ''));
        body.innerHTML = html;
      }

      const badges = this.querySelector('[data-ndd-badges]');
      if (!badges) return;
      badges.replaceChildren();
      this._regionList().forEach((name) => {
        const badge = document.createElement('span');
        badge.className = 'next-day-delivery__badge';
        badge.textContent = name;
        badges.appendChild(badge);
      });
    }

    _regionList() {
      return String(this.config.regions || '')
        .split(/[,;]+/)
        .map((value) => value.replace(/\.+$/, '').trim())
        .filter(Boolean);
    }

    _renderCopy() {
      const copy = this.config.copy || {};
      const isSecond = this.mode === 'second';
      const remaining = this._msUntilCutoff();
      const countdown = this._formatCountdown(remaining);
      const dateText = this._formatDate(this._deliveryDate());
      this._renderedDate = dateText;
      const cutoffText = this._formatCutoff();
      const city = this._cityName();
      const cutoffHtml = `<span data-ndd-cutoff>${this._escapeHtml(cutoffText)}</span>`;
      const dateHtml = `<span class="next-day-delivery__date" data-ndd-date>${this._escapeHtml(dateText)}</span>`;
      const cityHtml = `<b class="next-day-delivery__city" data-ndd-city>${this._escapeHtml(city)}</b>`;

      if (this.titleEl) {
        this.titleEl.textContent = (isSecond ? copy.secondTitle : copy.title) || '';
      }

      if (this.detailEl) {
        let html = this._escapeHtml((isSecond ? copy.secondDetail : copy.detail) || '');
        html = this._replaceToken(html, '[cutoff]', cutoffHtml);
        html = this._replaceToken(html, '[date]', dateHtml);
        this.detailEl.innerHTML = html;
      }

      if (this.estimateEl) {
        this.estimateEl.innerHTML = this._replaceToken(
          this._escapeHtml(copy.estimate || ''),
          '[city]',
          cityHtml
        );
      }

      if (this.estimateShortEl) {
        this.estimateShortEl.innerHTML = this._replaceToken(
          this._escapeHtml(copy.estimateShort || ''),
          '[city]',
          cityHtml
        );
      }

      this.querySelectorAll('[data-ndd-countdown]').forEach((el) => {
        el.textContent = countdown;
      });
    }

    _updateCountdown() {
      const mode = this._mode();
      if (mode !== this.mode) {
        this._evaluate();
        return;
      }
      if (mode === 'second') {
        // The promised date moves at midnight on the reference calendar.
        if (this._formatDate(this._deliveryDate()) !== this._renderedDate) this._renderCopy();
        return;
      }
      const remaining = this._msUntilCutoff();
      this.querySelectorAll('[data-ndd-countdown]').forEach((el) => {
        el.textContent = this._formatCountdown(remaining);
      });
    }

    _startTimer() {
      if (this.timer) return;
      this.timer = window.setInterval(() => this._updateCountdown(), 1000);
    }

    _stopTimer() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    _show() {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
    }

    _hide() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
    }

    _parsePostalList() {
      if (this._postalList) return this._postalList;
      this._postalList = String(this.config.postalCodes || '')
        .split(/[\s,;]+/)
        .map((value) => this._normalizePostal(value))
        .filter(Boolean);
      return this._postalList;
    }

    _normalizePostal(value) {
      return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    _postalMatches(postal) {
      const normalized = this._normalizePostal(postal);
      if (!normalized) return false;
      const fsa = normalized.slice(0, 3);
      return this._parsePostalList().some((allowed) => {
        if (allowed.length <= 3) return fsa === allowed;
        return normalized === allowed || normalized.startsWith(allowed);
      });
    }

    _haversineKm(lat1, lon1, lat2, lon2) {
      const toRad = (value) => (value * Math.PI) / 180;
      const earthKm = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return earthKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    _isGeoEligible() {
      const postals = [this.manualPostal, this.config.customerPostal, this.location?.postal];
      const postalMatch = postals.some((postal) => this._postalMatches(postal));

      const loc = this.location;
      const lat = Number(this.config.originLat);
      const lon = Number(this.config.originLng);
      const radius = Number(this.config.radiusKm);
      const hasOrigin = Number.isFinite(lat) && Number.isFinite(lon) && Number.isFinite(radius) && radius > 0;

      if (loc && hasOrigin && Number.isFinite(loc.lat) && Number.isFinite(loc.lon)) {
        if (loc.country && loc.country !== 'CA') {
          return postalMatch;
        }
        const distance = this._haversineKm(loc.lat, loc.lon, lat, lon);
        if (distance <= radius) return true;
      }

      return postalMatch;
    }

    async _resolveLocation() {
      const cached = this._readGeoCache();
      if (cached) return cached;

      const fromRebuy = await this._fetchRebuyGeo();
      if (fromRebuy) {
        this._writeGeoCache(fromRebuy);
        return fromRebuy;
      }

      return null;
    }

    async _fetchRebuyGeo() {
      try {
        const response = await fetch('https://geo.rebuyengine.com/?key=e9df80ad503aa371f73c30bed2d854fb099f876d');
        if (!response.ok) return null;
        const payload = await response.json();
        const data = payload && payload.data;
        if (!data || !data.latitude || !data.longitude || !data.postal_code) return null;
        return {
          lat: Number(data.latitude),
          lon: Number(data.longitude),
          postal: data.postal_code || '',
          country: String(data.country_code || '').toUpperCase(),
          city: data.city_name || ''
        };
      } catch (error) {
        return null;
      }
    }

    _readGeoCache() {
      try {
        const raw = sessionStorage.getItem('roveNextDayDeliveryGeoV2');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || Date.now() - parsed.ts > 30 * 60 * 1000) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    _writeGeoCache(location) {
      try {
        sessionStorage.setItem('roveNextDayDeliveryGeoV2', JSON.stringify({ ...location, ts: Date.now() }));
      } catch (error) {
        return;
      }
    }
  }

  customElements.define('next-day-delivery', NextDayDelivery);
}
